import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getPlatform,
  getUser,
  postIncentivesNudgePersonToSignupForReferralProgram,
} from "api/methods";
import { Models } from "@/api/types";
import { Text } from "@/atoms";
import { useTranslation } from "@/core/i18n";

type ReferralRewardsReferralHistoryEntryProps = {
  historyEntry: Models<"DisplayReferralHistoryItemForPerson">;
};

export const ReferralRewardsReferralHistoryEntry = ({
  historyEntry,
}: ReferralRewardsReferralHistoryEntryProps) => {
  const { t } = useTranslation();
  const [nudged, setNudged] = useState(false);
  const navigate = useNavigate();

  const { data: platform } = useQuery({ ...getPlatform.keyed() });

  const { data: user } = useQuery({ ...getUser.keyed() });

  const nudgeMutation = useMutation({
    mutationFn: async () => {
      return postIncentivesNudgePersonToSignupForReferralProgram({
        platformId: platform?.id ?? "",
        platformLocationId: user?.platformLocation?.id ?? "",
        personToNudgeId: historyEntry.personId ?? "",
        personFromNudgeId: user?.id ?? "",
      });
    },
    onSuccess: () => {
      setNudged(true);
      console.log("nudge successful");
    },
    onError: (error: any) => {
      console.error(error);
    },
  });

  const nudge = async () => {
    await nudgeMutation.mutateAsync();
  };

  const getDateText = () => {
    const now = dayjs();
    const updated = dayjs(historyEntry.updatedAtUtc);
    const diffDays = now.diff(updated, "day");

    if (diffDays === 0) {
      return updated.format("h:mm A");
    } else if (diffDays <= 7) {
      return t("referralRewardsReferralHistoryEntry.daysAgo", { count: diffDays });
    } else {
      return updated.format("MM/DD/YYYY");
    }
  };

  const handleBank = () => {
    // if (historyEntry.)
    navigate("/wallet");
  };

  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(historyEntry.absoluteShortUrlForSharing ?? "");
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const renderCta = () => {
    if (
      historyEntry.attributionState === "Unused" ||
      historyEntry.attributionState === "StartedSignup" ||
      historyEntry.attributionState === "ClickedReferralLink"
    ) {
      return (
        <button
          onClick={handleCopyLink}
          type="button"
          className="rounded-lg px-4 py-2 font-medium text-teal-600 transition-colors hover:bg-gray-200"
        >
          <Text
            variant="body2"
            text={copied ? t("common.copied") : t("referralRewardsReferralHistoryEntry.shareLink")}
          />
        </button>
      );
    }

    if (historyEntry.attributionState === "CompletedSignup") {
      return (
        <button
          onClick={nudge}
          type="button"
          className="rounded-lg px-4 py-2 font-medium text-teal-600 transition-colors hover:bg-gray-200"
        >
          <Text variant="body2" text={t("referralRewardsReferralHistoryEntry.nudge")} />
        </button>
      );
    }

    if (
      historyEntry.attributionState === "CompletedPurchase" ||
      historyEntry.attributionState === "CompletedVisit"
    ) {
      return (
        <button
          type="button"
          onClick={handleBank}
          className="rounded-lg px-4 py-2 font-medium text-teal-600 transition-colors hover:bg-gray-100"
        >
          <Text variant="body2" text={t("referralRewardsReferralHistoryEntry.banked")} />
        </button>
      );
    }

    if (nudged) {
      return (
        <div className="rounded-lg px-4 py-2 font-medium text-teal-600 transition-colors">
          <Text variant="body2" text={t("referralRewardsReferralHistoryEntry.nudged")} />
        </div>
      );
    }
  };

  return (
    <div className="ReferralRewardsReferralHistoryEntry border-top relative flex items-center justify-between border-gray-200 bg-white py-4 pl-6 pr-4">
      <div className="grid flex-1 gap-2">
        <div>
          <Text
            variant="body1"
            className="HistoryTitle font-bold text-black"
            text={historyEntry.historyTitle?.text ?? ""}
            component="p"
          />
          <Text
            variant="body2"
            className="HistorySubtitle text-gray-500"
            text={historyEntry.historySubtitle?.text ?? ""}
            component="p"
          />
        </div>
        <Text
          variant="body3"
          className="HistorySubtitle text-gray-500"
          text={getDateText()}
          component="p"
        />
      </div>

      <div className="ml-4">{renderCta()}</div>
    </div>
  );
};
